import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Button, Col, Form, Input, Row } from "antd";
import { contactStore } from "Pages/UserSettings/Stores/ContactStore";
import { notificationSuccess, notificationError, notificationInfo } from "utils/libHelpers/AntdNotification";
import { I18N_NS } from "Config/i18n";
import { FormCard } from "Components/Form/FormCard";
import { validateEmail } from "utils/validateEmail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useForm } from "antd/es/form/Form";
import { userInfoStore } from "Stores/UserInfoStore";

/**
 * View for Contact Information of User
 */
export const UserContact = observer(() => {
  const { t } = useTranslation([I18N_NS.USER_SETTINGS, I18N_NS.ADMIN]);
  const [form] = useForm();
  const [undeliverableReason, setUndeliverableReason] = useState(false);
  const [initialEmail, setInitialEmail] = useState("");
  const [sameEmail, setSameEmail] = useState(true);

  useEffect(() => {
    fetch();
  }, []);

  const fetch = async () => {
    try {
      await contactStore.fetch();
      form.setFieldsValue({ ...contactStore.data });
      setUndeliverableReason(contactStore.data.undeliverableReason);
      setInitialEmail(contactStore.data.email);
    } catch (error) {
      throw error;
    }
  };

  const checkEmail = (rule, values) => {
    if (!validateEmail(values)) {
      const msg = t("Please enter a valid email address");
      return Promise.reject(msg);
    } else if (values.undeliverableReason && values === values.email) {
      const msg = t("Your email adress caused undeliverable messages");
      return Promise.reject(msg);
    } else if (rule.field === "email" && !values) {
      const msg = t("Please provide an email");
      return Promise.reject(msg);
    } else {
      return Promise.resolve();
    }
  };

  const onConfirmMaildAdress = async () => {
    try {
      await contactStore.confirmMaildAdress();
      await fetch();
      notificationSuccess(t("Success"), t("Contact information has been saved"));
    } catch (err) {
      throw err;
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onValuesChange = (changedValues: any) => {
    const isEmail = Object.keys(changedValues).includes("email");
    if (isEmail) {
      const emailValue: string = changedValues["email"];
      if (emailValue === initialEmail) {
        setSameEmail(true);
      } else {
        setSameEmail(false);
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      await contactStore.submit(values);
      notificationSuccess(t("Success"), t("Contact information has been saved"));
    } catch (error) {
      if (error?.response?.data?.code === 2) {
        notificationInfo(t("Please note"), error.response.data.message, 0);
      } else {
        notificationError(t("Error"), t("Settings could not be saved"));
        throw error;
      }
    }
  };

  const contactInformationTitle = userInfoStore.isUnifiedUser
    ? t("Contact information for {{email}}", { email: initialEmail })
    : t("Contact information");

  const hasUnifiedAccount = userInfoStore.loginInfo.hasUnifiedAccount;
  let hasUnifiedAccountTooltip: string | undefined = undefined;
  if (hasUnifiedAccount) {
    hasUnifiedAccountTooltip = t(
      // eslint-disable-next-line max-len
      "You are linked to a unified account. Their display name and firstname is taken from the unified account and cannot be edited here."
    );
  }

  return (
    <Form onFinish={handleSubmit} form={form} layout="vertical" onValuesChange={onValuesChange}>
      <FormCard title={contactInformationTitle}>
        <Row gutter={{ xs: 8, lg: 16 }}>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Position")} name="companyRole">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item
              label={t("Surname")}
              name="surname"
              rules={[{ required: true, message: t("Please provide a surname") }]}
              tooltip={hasUnifiedAccountTooltip}
            >
              <Input disabled={hasUnifiedAccount} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("First name")} name="firstname" tooltip={hasUnifiedAccountTooltip}>
              <Input disabled={hasUnifiedAccount} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Company")} name="companyName">
              <Input />
            </Form.Item>
          </Col>
          {!userInfoStore.isUnifiedUser && (
            <Col xs={24} lg={8}>
              {undeliverableReason && sameEmail ? (
                <div
                  style={{ position: "absolute", left: 67, top: -15, cursor: "pointer", zIndex: 20 }}
                  onClick={onConfirmMaildAdress}
                >
                  <FontAwesomeIcon icon={["far", "exclamation-circle"]} size={"lg"} color="red" fixedWidth={true} />
                  <Button type="default" icon>
                    {t("Confirm this mail address")}
                  </Button>
                </div>
              ) : null}
              <Form.Item label={t("E-Mail")} name="email" rules={[{ validator: checkEmail, required: true }]}>
                <Input status={undeliverableReason ? "error" : null} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} lg={8}>
            <Form.Item label={t("Street")} name="street">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Area Code")} name="postalCode">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Location")} name="city">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Telephone")} name="phoneNumber">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Fax")} name="fax">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label={t("Mobile")} name="mobileNumber">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </FormCard>
      <div className="text-right">
        <Button type="primary" htmlType="submit" loading={contactStore.state === "LOADING"}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );
});
